/**
 * ImageCarousel — Product image carousel with hover-zoom lens (desktop)
 * and thumbnail strip navigation. Opens ImageLightbox on click.
 *
 * Props:
 *   images        — array of { src, alt, thumbnailSrc }
 *   productName   — string
 *   categorySlug  — string (fallback placeholder path)
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import ImageLightbox from '@components/product/ImageLightbox';

export default function ImageCarousel({ images, productName, categorySlug }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isZooming, setIsZooming] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  const containerRef = useRef(null);

  // Track lg breakpoint for hover-zoom
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    setIsLargeScreen(mql.matches);
    const handler = (e) => setIsLargeScreen(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const safeImages = Array.isArray(images) && images.length > 0 ? images : null;
  const hasMultiple = safeImages && safeImages.length > 1;
  const currentImage = safeImages ? safeImages[activeIndex] : null;

  // --- Navigation ---
  const goNext = useCallback(() => {
    if (!safeImages) return;
    setActiveIndex((prev) => (prev + 1) % safeImages.length);
  }, [safeImages]);

  const goPrev = useCallback(() => {
    if (!safeImages) return;
    setActiveIndex((prev) => (prev - 1 + safeImages.length) % safeImages.length);
  }, [safeImages]);

  // --- Keyboard ---
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (safeImages) setLightboxOpen(true);
      }
    },
    [goNext, goPrev, safeImages],
  );

  // --- Hover zoom ---
  const handleMouseMove = useCallback(
    (e) => {
      if (!isLargeScreen || !containerRef.current || !safeImages) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setZoomPosition({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
      setIsZooming(true);
    },
    [isLargeScreen, safeImages],
  );

  const handleMouseLeave = useCallback(() => {
    setIsZooming(false);
  }, []);

  const handleImageClick = useCallback(() => {
    if (safeImages) setLightboxOpen(true);
  }, [safeImages]);

  const handleLightboxClose = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const handleLightboxIndexChange = useCallback((newIndex) => {
    setActiveIndex(newIndex);
  }, []);

  // --- Placeholder (no images) ---
  if (!safeImages) {
    return (
      <div className="relative">
        <div className="relative overflow-hidden bg-n1 aspect-[4/3] flex items-center justify-center">
          <div className="text-center text-n4">
            <svg
              className="mx-auto mb-2 w-16 h-16 text-n3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z"
              />
            </svg>
            <p className="text-sm font-sandvik-text">No image available</p>
          </div>
        </div>
      </div>
    );
  }

  // Lens position (centered on cursor)
  const lensSize = 128; // w-32 = 8rem = 128px

  return (
    <div className="relative" tabIndex={0} onKeyDown={handleKeyDown}>
      {/* Main image area + zoom result side-by-side on lg */}
      <div className="flex gap-4">
        {/* Main image container */}
        <div className="flex-1 relative">
          <div
            ref={containerRef}
            className="relative overflow-hidden bg-n1 aspect-[4/3] cursor-zoom-in"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={handleImageClick}
            role="button"
            aria-label={`View ${productName || 'product'} image ${activeIndex + 1} of ${safeImages.length} fullscreen`}
          >
            <img
              src={currentImage.src}
              alt={currentImage.alt || `${productName || 'Product'} image ${activeIndex + 1}`}
              className="w-full h-full object-contain"
              draggable={false}
            />

            {/* Zoom lens overlay (desktop only) */}
            {isZooming && isLargeScreen && (
              <div
                className="absolute w-32 h-32 rounded-full border-2 border-electric-blue pointer-events-none bg-white/20"
                style={{
                  left: `calc(${zoomPosition.x}% - ${lensSize / 2}px)`,
                  top: `calc(${zoomPosition.y}% - ${lensSize / 2}px)`,
                }}
              />
            )}

            {/* Navigation arrows */}
            {hasMultiple && (
              <>
                <button
                  type="button"
                  className="absolute top-1/2 -translate-y-1/2 left-2 bg-white/80 hover:bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-md text-carbon-black text-lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    goPrev();
                  }}
                  aria-label="Previous image"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="absolute top-1/2 -translate-y-1/2 right-2 bg-white/80 hover:bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-md text-carbon-black text-lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    goNext();
                  }}
                  aria-label="Next image"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Zoom result panel (lg+ only, visible while hovering) */}
        {isZooming && isLargeScreen && currentImage && (
          <div className="hidden lg:block w-80 h-80 overflow-hidden border border-n3 bg-n1 flex-shrink-0">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `url(${currentImage.src})`,
                backgroundSize: '250%',
                backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                backgroundRepeat: 'no-repeat',
              }}
            />
          </div>
        )}
      </div>

      {/* Thumbnail strip */}
      {hasMultiple && (
        <div className="flex gap-2 mt-3 overflow-x-auto" role="tablist" aria-label="Product image thumbnails">
          {safeImages.map((img, idx) => (
            <button
              key={idx}
              type="button"
              role="tab"
              aria-selected={idx === activeIndex}
              aria-label={`View image ${idx + 1}`}
              className={`w-16 h-16 rounded border-2 cursor-pointer object-contain bg-n1 flex-shrink-0 overflow-hidden ${
                idx === activeIndex
                  ? 'border-electric-blue'
                  : 'border-transparent hover:border-n3'
              }`}
              onClick={() => setActiveIndex(idx)}
            >
              <img
                src={img.thumbnailSrc || img.src}
                alt={img.alt || `Thumbnail ${idx + 1}`}
                className="w-full h-full object-contain"
                draggable={false}
              />
            </button>
          ))}
        </div>
      )}

      {/* Image counter */}
      {hasMultiple && (
        <p className="text-xs text-n5 mt-1 text-center font-sandvik-text">
          {activeIndex + 1} / {safeImages.length}
        </p>
      )}

      {/* Lightbox */}
      {lightboxOpen && (
        <ImageLightbox
          images={safeImages}
          activeIndex={activeIndex}
          onClose={handleLightboxClose}
          onIndexChange={handleLightboxIndexChange}
        />
      )}
    </div>
  );
}
